import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@v2/lib/utils";
import { VariantProps, cva } from "class-variance-authority";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { SelectInput, SelectItem } from "@cmp-src/form/SelectInput";

const TabsContext = React.createContext<{
  value?: string;
  onValueChange?: (value: string) => void;
}>({ value: "" });
const useTabs = () => {
  return React.useContext(TabsContext);
};
const tabsVariants = cva("", {
  variants: {
    variant: {
      settingsTabs: "flex h-full flex-col",
      default: ""
    }
  }
});

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
    VariantProps<typeof tabsVariants>
>(({ className, variant = "default", ...props }, ref) => (
  <TabsContext.Provider
    value={{ value: props.value, onValueChange: props.onValueChange }}
  >
    <TabsPrimitive.Root
      ref={ref}
      className={cn(tabsVariants({ variant }), className)}
      {...props}
    />
  </TabsContext.Provider>
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsListVariants = cva(
  "flex flex-row flex-wrap items-center justify-start gap-x-4 gap-y-2 rounded-md text-muted-foreground active:border-none active:ring-0 focus:border-none focus:after:border-none focus:after:ring-0 focus:outline-none focus:ring-0 focus:outline-none selected:border-none selected:ring-0",
  {
    variants: {
      variant: {
        settingsTabs: "mt-settings-post-title mb-4",
        default: ""
      }
    }
  }
);
export type TabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> &
  VariantProps<typeof tabsListVariants> & {
    showTabsAsDropdownInMobile?: boolean;
    value?: string;
    onValueChange?: (value: string) => void;
    getOptionLabel?: (trigger: React.ReactElement) => string;
  };

const defaultGetOptionLabel = (child: React.ReactElement): string => {
  if (child.props?.["data-label"]) return String(child.props["data-label"]);
  const c = child.props?.children;
  if (typeof c === "string" || typeof c === "number") return String(c);
  if (Array.isArray(c)) {
    const flat = c
      .map((n: any) =>
        typeof n === "string" || typeof n === "number" ? n : ""
      )
      .join(" ")
      .trim();
    if (flat) return flat;
  }
  return child.props?.value ?? "";
};

const DropdownTabsList: React.FC<TabsListProps> = ({
  children,
  getOptionLabel = defaultGetOptionLabel,
  className
}) => {
  const { value, onValueChange } = useTabs();
  const options = React.Children.toArray(children)
    .filter(Boolean)
    .map((child) => {
      if (!React.isValidElement(child)) return null;
      // accetta sia TabsPrimitive.Trigger che wrapper che lo ritornano
      const isTrigger =
        (child.type as any) === TabsPrimitive.Trigger ||
        (child.type as any)?.displayName === TabsPrimitive.Trigger.displayName;

      if (!isTrigger) return null;
      const v = child.props?.value as string | undefined;
      if (!v) return null;

      return {
        value: v,
        label: getOptionLabel(child),
        disabled: Boolean(child.props?.disabled)
      };
    })
    .filter(Boolean) as {
    value: string;
    label: string;
    disabled: boolean;
  }[];

  return (
    <div className={cn("w-full", className)}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
      <label className="sr-only">Select tab</label>
      <SelectInput
        // se value non è passato, prova a usare la prima option
        value={value ?? options[0]?.value ?? ""}
        onChangeValue={onValueChange}
        aria-label="Tabs mobile select"
        className={"rounded-lg border-1 border-neutral-200"}
      >
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectInput>
    </div>
  );
};

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(
  (
    { className, variant = "default", showTabsAsDropdownInMobile, ...props },
    ref
  ) => {
    const { isBreakpoint } = useBreakpoint();
    const isMobile = !isBreakpoint("md");

    if (showTabsAsDropdownInMobile && isMobile) {
      return <DropdownTabsList className={className} {...props} />;
    }

    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    );
  }
);
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  cn(
    "relative inline-flex items-center justify-center whitespace-nowrap",
    "rounded-sm py-1.5 font-medium text-[14px] ring-offset-background",
    "transition-none focus-visible:outline-none focus-visible:ring-0",
    "disabled:pointer-events-none disabled:opacity-50"
  ),
  {
    variants: {
      variant: {
        default:
          "data-[state=active]:bg-background data-[state=active]:text-neutral-1000 data-[state=active]:font-[600] px-2 first:pl-0",
        minimalistic: cn(
          "data-[state=active]:text-primary data-[state=active]:font-[600] data-[state=active]:after:absolute",
          "data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-[2px] data-[state=active]:after:bg-primary",
          "data-[state=active]:after:rounded-full text-md [.tactic-v2_&]:text-sm",
          "[.tactic-v2_&]:data-[state=active]:text-neutral-1000 [.tactic-v2_&]:data-[state=active]:after:bg-neutral-1000"
        )
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant = "default", ...props }, ref) => {
  const { children, ...rest } = props;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), "group/root", className)}
      {...rest}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const tabsContentVariants = cva(
  cn(
    "ring-offset-background focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
    "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
  ),
  {
    variants: {
      variant: {
        settingsTabsContent: "flex-1 overflow-hidden",
        default: ""
      }
    }
  }
);

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> &
    VariantProps<typeof tabsContentVariants>
>(({ className, variant = "default", ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(tabsContentVariants({ variant }), className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
